import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import {
  IMPORT_STATE,
  type ImportHistoryItemT,
  type TrustpilotBusinessUnit,
  type TrustpilotInfo,
} from '../types';

const mapTrustpilotInfo = (data: unknown): TrustpilotInfo | null => {
  if (!data || typeof data !== 'object') return null;

  const info = data as {
    workspace_id?: string;
    alias?: string;
    state?: IMPORT_STATE;
    import_state?: IMPORT_STATE;
    history?: ImportHistoryItemT[];
    business_units?: TrustpilotBusinessUnit[];
  };

  if (!info.alias) return null;

  return {
    workspace_id: info.workspace_id || info.alias,
    alias: info.alias,
    import_state: info.import_state || info.state || IMPORT_STATE.NOT_STARTED,
    history: info.history || [],
    business_units: info.business_units || [],
  };
};

export const useTrustpilot = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspaceAlias = currWorkspace?.alias;

  if (!workspaceAlias) throw new Error('Workspace alias is required');

  const queryKey = getIntegrationQueryKeys(
    SOURCE_TYPE.TRUSTPILOT,
    workspaceAlias,
  );

  const { data: trustpilotWorkspace } = useQuery({
    queryKey,
    queryFn: async () =>
      mapTrustpilotInfo(
        await service.trustpilot.getTrustpilotInfo(workspaceAlias),
      ),
    enabled,
  });

  const trustpilotAlias = trustpilotWorkspace?.alias ?? '';

  const { mutate: disconnectTrustpilotMutation } = useMutation({
    mutationFn: () => service.trustpilot.deleteTrustpilotInfo(trustpilotAlias),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const { mutate: requestImportMutation } = useMutation({
    mutationFn: ({ start_date, end_date }: NullableDateRangeT) => {
      if (!start_date || !end_date) return Promise.resolve(false);
      const startDate: string = start_date;
      const endDate: string = end_date;
      return service.trustpilot.importWorkspaceFeedback(
        trustpilotAlias,
        startDate,
        endDate,
      );
    },
    onSuccess: () => {
      queryClient.setQueryData<TrustpilotInfo | null>(queryKey, (current) => {
        if (!current) return current;

        return {
          ...current,
          import_state: IMPORT_STATE.IMPORTING,
        };
      });
    },
  });

  const setTrustpilotWorkspace = (workspace: TrustpilotInfo) => {
    queryClient.setQueryData<TrustpilotInfo | null>(queryKey, workspace);
  };

  const setTrustpilotBusinessUnits = (
    businessUnits: TrustpilotBusinessUnit[],
  ) => {
    queryClient.setQueryData<TrustpilotInfo | null>(queryKey, (current) => {
      if (!current) return current;

      return {
        ...current,
        business_units: businessUnits,
      };
    });
  };

  return {
    trustpilotWorkspace,
    disconnectTrustpilotMutation,
    requestImportMutation,
    setTrustpilotWorkspace,
    setTrustpilotBusinessUnits,
  };
};
